import { IDentalData } from '@dataTypes/chartingFeature.types'
import type { BridgeConnectorType } from '@utils/bridge-connector.utils'

/**
 * Type guard to check if data is IDentalData[] format
 */
export const isIDentalDataArray = (data: unknown): data is IDentalData[] => {
  if (!Array.isArray(data)) {
    return false
  }

  return data.every((item) => {
    return (
      typeof item === 'object' &&
      item !== null &&
      'tooth_number' in item &&
      'condition' in item &&
      'recurrent' in item &&
      // Accept both string and number for tooth_number (API returns number, type expects string)
      (typeof item.tooth_number === 'string' ||
        typeof item.tooth_number === 'number') &&
      typeof item.condition === 'string' &&
      typeof item.recurrent === 'boolean' &&
      (item.type === null || typeof item.type === 'string') &&
      (item.material === null || typeof item.material === 'string') &&
      (item.surface === null || typeof item.surface === 'string')
    )
  })
}

/**
 * Get all dental data entries for a specific tooth
 */
export const getDentalDataForTooth = (
  dental_data: IDentalData[],
  toothNumber: number,
): IDentalData[] => {
  return dental_data.filter((data) => {
    // Handle both string and number types for tooth_number
    const dataToothNumber =
      typeof data.tooth_number === 'string'
        ? data.tooth_number
        : String(data.tooth_number)
    return dataToothNumber === toothNumber.toString()
  })
}

/**
 * Convert dental_data array to a map by tooth number
 */
export const getDentalDataMap = (
  dental_data: IDentalData[],
): Record<number, IDentalData[]> => {
  const map: Record<number, IDentalData[]> = {}

  dental_data.forEach((data) => {
    // Handle both string and number types for tooth_number
    const toothNumberStr =
      typeof data.tooth_number === 'string'
        ? data.tooth_number
        : String(data.tooth_number)
    const toothNum = parseInt(toothNumberStr, 10)
    if (!map[toothNum]) {
      map[toothNum] = []
    }
    map[toothNum].push(data)
  })

  return map
}

/**
 * Filter dental data by layers (dental/endo)
 */
export const filterDentalDataByLayers = (
  dental_data: IDentalData[],
  layers: Record<string, boolean>,
): IDentalData[] => {
  // Endo conditions
  const ENDO_CONDITIONS = [
    'apicoectomy',
    'periapical',
    'periradicular',
    'root_canal',
    'pulpotomy',
    'pulpectomy',
    'sinus_tract',
  ]

  // If both layers are checked or neither, return all data
  if ((layers.dental && layers.endo) || (!layers.dental && !layers.endo)) {
    return dental_data
  }

  return dental_data.filter((data) => {
    const isEndoCondition =
      ENDO_CONDITIONS.includes(data.condition) ||
      data.condition.startsWith('periradicular_')

    if (layers.dental && !layers.endo) {
      return !isEndoCondition
    }

    if (!layers.dental && layers.endo) {
      return isEndoCondition
    }

    return true
  })
}

/**
 * Add a condition to dental_data array
 * Updates existing entries when appropriate:
 * - If adding a surface to a base entry (no surface), updates that entry
 * - If adding a material-only entry (no surface) for material-exclusive conditions,
 *   updates ALL existing surface entries for that condition to include the material
 * - If adding a material to a specific surface entry, updates that entry
 * - If changing a material, updates all existing entries with the old material
 * - Otherwise, creates a new entry
 */
export const addConditionToDentalData = (
  dental_data: IDentalData[],
  toothNumber: number,
  condition: string,
  type: string | null = null,
  material: string | null = null,
  surface: string | null = null,
  recurrent: boolean = false,
): IDentalData[] => {
  let normalizedCondition = condition
  let normalizedType = type
  if (normalizedCondition === 'bridge' && type?.endsWith('_bridge')) {
    normalizedType = null
  } else if (normalizedCondition === 'abutment' && type?.endsWith('_bridge')) {
    normalizedCondition = 'bridge'
    normalizedType = null
  }

  const toothNumberStr = toothNumber.toString()

  // Enforce bridge/abutment mutual exclusivity per tooth.
  if (normalizedCondition === 'bridge' || normalizedCondition === 'abutment') {
    const opposingCondition =
      normalizedCondition === 'bridge' ? 'abutment' : 'bridge'
    const filteredDentalData = dental_data.filter(
      (data) =>
        !(
          String(data.tooth_number) === toothNumberStr &&
          data.condition === opposingCondition
        ),
    )

    if (filteredDentalData.length !== dental_data.length) {
      return addConditionToDentalData(
        filteredDentalData,
        toothNumber,
        normalizedCondition,
        normalizedType,
        material,
        surface,
        recurrent,
      )
    }
  }

  // Conditions that have mutually exclusive materials (should update existing entry when material changes)
  const materialExclusiveConditions = [
    'inlay',
    'onlay',
    'crown',
    'filling',
    'bridge',
    'abutment',
    'veneer',
  ]
  const isMaterialExclusive =
    materialExclusiveConditions.includes(normalizedCondition)

  // Check if we're adding a surface
  if (surface !== null) {
    // First, check if there's an existing material-only entry for this condition
    // If so, use that material for the new surface entry
    if (material === null && isMaterialExclusive) {
      const materialOnlyEntry = dental_data.find((data) => {
        const dataToothNumber =
          typeof data.tooth_number === 'string'
            ? data.tooth_number
            : String(data.tooth_number)
        return (
          dataToothNumber === toothNumberStr &&
          data.condition === normalizedCondition &&
          data.type === normalizedType &&
          data.surface === null &&
          data.material !== null &&
          data.recurrent === recurrent
        )
      })

      if (materialOnlyEntry && materialOnlyEntry.material) {
        // Use the material from the material-only entry
        material = materialOnlyEntry.material
        // Don't update the material-only entry - create a new surface entry instead
        // Continue to create new entry below
      }
    }

    // Check if there's a base entry (no surface) with matching material to update
    // Only update if it's NOT a material-only entry (material === null means it's a true base entry)
    if (material === null) {
      const baseEntryIndex = dental_data.findIndex((data) => {
        const dataToothNumber =
          typeof data.tooth_number === 'string'
            ? data.tooth_number
            : String(data.tooth_number)
        return (
          dataToothNumber === toothNumberStr &&
          data.condition === normalizedCondition &&
          data.type === normalizedType &&
          data.material === null &&
          data.surface === null &&
          data.recurrent === recurrent
        )
      })

      if (baseEntryIndex !== -1) {
        // Update the existing base entry with the surface
        const updatedData = [...dental_data]
        updatedData[baseEntryIndex] = {
          ...updatedData[baseEntryIndex],
          surface,
        }
        return updatedData
      }
    }
  }

  // Check if we're adding a material
  if (material !== null) {
    // If adding a material-only entry (no surface), update ALL existing surface entries for this condition
    if (surface === null && isMaterialExclusive) {
      const updatedData = [...dental_data]
      let hasUpdates = false

      // Find and update all existing entries for this condition
      // This includes entries with surfaces and base entries (material applies to all)
      dental_data.forEach((data, index) => {
        const dataToothNumber =
          typeof data.tooth_number === 'string'
            ? data.tooth_number
            : String(data.tooth_number)
        const matchesCondition =
          dataToothNumber === toothNumberStr &&
          data.condition === normalizedCondition &&
          data.type === normalizedType &&
          data.recurrent === recurrent

        if (matchesCondition) {
          // Update this entry with the new material (applies to all surfaces and base entries)
          updatedData[index] = {
            ...updatedData[index],
            material,
          }
          hasUpdates = true
        }
      })

      if (hasUpdates) {
        // Also check if we need to create/update a material-only base entry
        const hasBaseEntry = updatedData.some((data) => {
          const dataToothNumber =
            typeof data.tooth_number === 'string'
              ? data.tooth_number
              : String(data.tooth_number)
          return (
            dataToothNumber === toothNumberStr &&
            data.condition === normalizedCondition &&
            data.type === normalizedType &&
            data.surface === null &&
            data.material === material &&
            data.recurrent === recurrent
          )
        })

        if (!hasBaseEntry) {
          // Add a material-only base entry
          const newEntry: IDentalData = {
            tooth_number: toothNumberStr,
            condition,
            type,
            material,
            surface: null,
            recurrent,
          }
          return [...updatedData, newEntry]
        }

        return updatedData
      }
    }

    // If adding a material to a specific surface entry, update that specific entry
    if (surface !== null) {
      const baseEntryIndex = dental_data.findIndex((data) => {
        const dataToothNumber =
          typeof data.tooth_number === 'string'
            ? data.tooth_number
            : String(data.tooth_number)
        return (
          dataToothNumber === toothNumberStr &&
          data.condition === normalizedCondition &&
          data.type === normalizedType &&
          data.surface === surface &&
          data.material === null &&
          data.recurrent === recurrent
        )
      })

      if (baseEntryIndex !== -1) {
        // Update the existing entry with the material
        const updatedData = [...dental_data]
        updatedData[baseEntryIndex] = {
          ...updatedData[baseEntryIndex],
          material,
        }
        return updatedData
      }
    }
  }

  // No matching entry found, create a new entry
  const newEntry: IDentalData = {
    tooth_number: toothNumberStr,
    condition: normalizedCondition,
    type: normalizedType,
    material,
    surface,
    recurrent,
  }

  return [...dental_data, newEntry]
}

/**
 * Remove a condition from dental_data array
 */
export const removeConditionFromDentalData = (
  dental_data: IDentalData[],
  toothNumber: number,
  condition: string,
  type: string | null = null,
  material: string | null = null,
  surface: string | null = null,
): IDentalData[] => {
  let normalizedCondition = condition
  let normalizedType = type
  const normalizedMaterial = material?.toLowerCase() ?? null
  const normalizedSurface = surface?.toLowerCase() ?? null
  if (normalizedCondition === 'bridge' && type?.endsWith('_bridge')) {
    normalizedType = null
  } else if (normalizedCondition === 'abutment' && type?.endsWith('_bridge')) {
    normalizedCondition = 'bridge'
    normalizedType = null
  }

  return dental_data.filter((data) => {
    // Handle both string and number types for tooth_number
    const dataToothNumber =
      typeof data.tooth_number === 'string'
        ? data.tooth_number
        : String(data.tooth_number)
    if (dataToothNumber !== toothNumber.toString()) {
      return true
    }

    // Match based on condition and optional parameters
    if (data.condition !== normalizedCondition) {
      return true
    }

    if (
      normalizedType !== null &&
      (data.type?.toLowerCase() ?? null) !== normalizedType.toLowerCase()
    ) {
      return true
    }

    if (
      normalizedMaterial !== null &&
      (data.material?.toLowerCase() ?? null) !== normalizedMaterial
    ) {
      return true
    }

    if (
      normalizedSurface !== null &&
      (data.surface?.toLowerCase() ?? null) !== normalizedSurface
    ) {
      return true
    }

    // If all match, filter out this entry
    return false
  })
}

/**
 * Check if a specific condition exists in dental_data
 */
export const hasCondition = (
  dental_data: IDentalData[],
  toothNumber: number,
  condition: string,
  type: string | null = null,
  material: string | null = null,
  surface: string | null = null,
): boolean => {
  let normalizedCondition = condition
  let normalizedType = type
  const normalizedMaterial = material?.toLowerCase() ?? null
  const normalizedSurface = surface?.toLowerCase() ?? null
  if (normalizedCondition === 'bridge' && type?.endsWith('_bridge')) {
    normalizedType = null
  } else if (normalizedCondition === 'abutment' && type?.endsWith('_bridge')) {
    normalizedCondition = 'bridge'
    normalizedType = null
  }

  return dental_data.some((data) => {
    // Handle both string and number types for tooth_number
    const dataToothNumber =
      typeof data.tooth_number === 'string'
        ? data.tooth_number
        : String(data.tooth_number)
    if (dataToothNumber !== toothNumber.toString()) {
      return false
    }

    if (data.condition !== normalizedCondition) {
      return false
    }

    if (
      normalizedType !== null &&
      (data.type?.toLowerCase() ?? null) !== normalizedType.toLowerCase()
    ) {
      return false
    }

    if (
      normalizedMaterial !== null &&
      (data.material?.toLowerCase() ?? null) !== normalizedMaterial
    ) {
      return false
    }

    if (
      normalizedSurface !== null &&
      (data.surface?.toLowerCase() ?? null) !== normalizedSurface
    ) {
      return false
    }

    return true
  })
}

/**
 * Check if any condition with a specific prefix exists
 */
export const hasConditionStartingWith = (
  dental_data: IDentalData[],
  toothNumber: number,
  prefix: string,
): boolean => {
  return dental_data.some((data) => {
    // Handle both string and number types for tooth_number
    const dataToothNumber =
      typeof data.tooth_number === 'string'
        ? data.tooth_number
        : String(data.tooth_number)
    return (
      dataToothNumber === toothNumber.toString() &&
      data.condition.startsWith(prefix)
    )
  })
}

/**
 * Remove all conditions starting with a specific prefix
 */
export const removeConditionsStartingWith = (
  dental_data: IDentalData[],
  toothNumber: number,
  prefix: string,
): IDentalData[] => {
  return dental_data.filter((data) => {
    // Handle both string and number types for tooth_number
    const dataToothNumber =
      typeof data.tooth_number === 'string'
        ? data.tooth_number
        : String(data.tooth_number)
    return (
      dataToothNumber !== toothNumber.toString() ||
      !data.condition.startsWith(prefix)
    )
  })
}

/**
 * Helper function to filter dental data by condition type
 */
export const getConditionsByType = (
  toothData: IDentalData[],
  conditionType: string,
): IDentalData[] => {
  if (conditionType === 'bridge') {
    return toothData.filter(
      (data) => data.condition === 'bridge' || data.condition === 'abutment',
    )
  }
  return toothData.filter((data) => data.condition === conditionType)
}

/**
 * Helper function to extract surfaces from condition data
 */
const extractSurfaces = (data: IDentalData[]): string[] => {
  return data
    .map((item) => item.surface)
    .filter((surface): surface is string => surface !== null)
}

/**
 * Type definitions for structured condition objects
 */
export type CariesCondition = {
  surfaces: string[]
  type?: 'D1' | 'D2' | 'D3' | 'E1' | 'E2'
  recurrent: boolean
  data: IDentalData[]
}

export type InlayCondition = {
  surfaces: string[]
  material?: 'gold' | 'zirconia' | 'ceramic'
  data: IDentalData[]
}

export type OnlayCondition = {
  surfaces: string[]
  material?: 'gold' | 'zirconia' | 'ceramic'
  data: IDentalData[]
}

export type FillingCondition = {
  surfaces: string[]
  material?: 'ceramic' | 'composite' | 'gold' | 'amalgam' | 'temporary'
  data: IDentalData[]
}

export type DriftedCondition = {
  surfaces: string[]
  data: IDentalData[]
}

export type CrownCondition = {
  material?: 'zirconia' | 'pfm' | 'pfz' | 'metal' | 'ceramic'
  data: IDentalData[]
}

export type VeneerCondition = {
  present: boolean
  material?: 'ceramic' | 'composite' | 'zirconia'
  data: IDentalData[]
}

export type ErosionCondition = {
  general: boolean
  buccal: boolean
  palatal: boolean
  lingual: boolean
  occlusal: boolean
  data: IDentalData[]
}

export type PeriradicularCondition = {
  severity?: 'slight' | 'moderate' | 'severe'
  data: IDentalData[]
}

export type ImpactedCondition = {
  direction?: 'left' | 'right' | 'down' | 'up'
  data: IDentalData[]
}

export type RecessionCondition = {
  type?: 'class1' | 'class2' | 'class3' | 'class4'
  data: IDentalData[]
}

export type AttritionCondition = {
  severity?: 'slight' | 'moderate' | 'severe'
  data: IDentalData[]
}

export type AbrasionCondition = {
  severity?: 'slight' | 'moderate' | 'severe'
  buccal: boolean
  palatal: boolean
  lingual: boolean
  data: IDentalData[]
}

export type BridgeCondition = {
  connector?: BridgeConnectorType
  material?: 'zirconia' | 'pfm' | 'pfz' | 'metal' | 'ceramic' | null
  data: IDentalData[]
}

export type FurcationCondition = {
  level?: 'C1' | 'C2' | 'C3'
  areas?: string
  data: IDentalData[]
}

export type FractureCondition = {
  types: Array<
    | 'mesiobuccal'
    | 'distobuccal'
    | 'mesiolingual'
    | 'distolingual'
    | 'mesiopalatal'
    | 'distopalatal'
    | 'incisal'
    | 'mifp'
    | 'difp'
    | 'mifl'
    | 'difl'
  >
  type?:
    | 'mesiobuccal'
    | 'distobuccal'
    | 'mesiolingual'
    | 'distolingual'
    | 'mesiopalatal'
    | 'distopalatal'
    | 'incisal'
    | 'mifp'
    | 'difp'
    | 'mifl'
    | 'difl'
  recurrent: boolean
  data: IDentalData[]
}

export type CrackCondition = {
  types: Array<
    | 'vertical_root'
    | 'horizontal_root'
    | 'vertical_crown_buccal'
    | 'vertical_crown_lingual'
    | 'vertical_crown_palatal'
    | 'vertical_crown_occlusal'
    | 'horizontal_crown_buccal'
    | 'horizontal_crown_lingual'
    | 'horizontal_crown_palatal'
    | 'horizontal_crown_occlusal'
  >
  type?:
    | 'vertical_root'
    | 'horizontal_root'
    | 'vertical_crown_buccal'
    | 'vertical_crown_lingual'
    | 'vertical_crown_palatal'
    | 'vertical_crown_occlusal'
    | 'horizontal_crown_buccal'
    | 'horizontal_crown_lingual'
    | 'horizontal_crown_palatal'
    | 'horizontal_crown_occlusal'
  recurrent: boolean
  data: IDentalData[]
}

export type BooleanCondition = {
  present: boolean
  data: IDentalData[]
}

/**
 * Extract properties needed for SVG manipulation from dental data
 * This function now returns structured objects that better utilize IDentalData
 */
export const getConditionProps = (
  dental_data: IDentalData[],
  toothNumber: number,
): {
  caries: CariesCondition
  recurring_caries: CariesCondition
  inlay: InlayCondition
  onlay: OnlayCondition
  filling: FillingCondition
  drifted: DriftedCondition
  crown: CrownCondition
  erosion: ErosionCondition
  apicoectomy: BooleanCondition
  periapical: BooleanCondition
  periradicular: PeriradicularCondition
  root_canal: BooleanCondition
  pulpotomy: BooleanCondition
  pulpectomy: BooleanCondition
  impacted: ImpactedCondition
  supernumerary: BooleanCondition
  sinusTract: BooleanCondition
  retainedPrimary: BooleanCondition
  recession: RecessionCondition
  attrition: AttritionCondition
  abrasion: AbrasionCondition
  veneer: VeneerCondition
  retainer: BooleanCondition
  bridge: BridgeCondition
  sealants: BooleanCondition
  missing: BooleanCondition
  implant: BooleanCondition
  furcation: FurcationCondition
  crack: CrackCondition
  fracture: FractureCondition
} => {
  const toothData = getDentalDataForTooth(dental_data, toothNumber)

  // Group conditions by type - now we keep the actual IDentalData objects
  const cariesData = getConditionsByType(toothData, 'caries')
  const recurringCariesData = getConditionsByType(toothData, 'recurring_caries')
  const inlayData = getConditionsByType(toothData, 'inlay')
  const onlayData = getConditionsByType(toothData, 'onlay')
  const fillingData = getConditionsByType(toothData, 'filling')
  const driftedData = getConditionsByType(toothData, 'drifted')
  const crownData = getConditionsByType(toothData, 'crown')
  const erosionData = getConditionsByType(toothData, 'erosion')
  const periradicularData = getConditionsByType(toothData, 'periradicular')
  const impactedData = getConditionsByType(toothData, 'impacted')
  const recessionData = getConditionsByType(toothData, 'recession')
  const attritionData = getConditionsByType(toothData, 'attrition')
  const abrasionData = getConditionsByType(toothData, 'abrasion')
  const bridgeData = getConditionsByType(toothData, 'bridge')
  const furcationData = getConditionsByType(toothData, 'furcation')
  const crackData = getConditionsByType(toothData, 'crack')
  const fractureData = getConditionsByType(toothData, 'fracture')
  const apicoectomyData = getConditionsByType(toothData, 'apicoectomy')
  const periapicalData = getConditionsByType(toothData, 'periapical')
  const rootCanalData = getConditionsByType(toothData, 'root_canal')
  const pulpotomyData = getConditionsByType(toothData, 'pulpotomy')
  const pulpectomyData = getConditionsByType(toothData, 'pulpectomy')
  const supernumeraryData = getConditionsByType(toothData, 'supernumerary')
  const sinusTractData = getConditionsByType(toothData, 'sinus_tract')
  const retainerData = getConditionsByType(toothData, 'retainer')
  const veneerData = getConditionsByType(toothData, 'veneer')
  const sealantsData = getConditionsByType(toothData, 'sealants')
  const missingData = getConditionsByType(toothData, 'missing')
  const implantData = getConditionsByType(toothData, 'implant')

  // Build structured condition objects that utilize the IDentalData objects
  return {
    caries: {
      surfaces: extractSurfaces(cariesData),
      type: cariesData.find((data) => data.type !== null)?.type
        ? (cariesData
            .find((data) => data.type !== null)!
            .type!.toUpperCase() as 'D1' | 'D2' | 'D3' | 'E1' | 'E2')
        : undefined,
      recurrent: cariesData.some((data) => data.recurrent === true),
      data: cariesData,
    },
    recurring_caries: {
      surfaces: extractSurfaces(recurringCariesData),
      type: recurringCariesData.find((data) => data.type !== null)?.type
        ? (recurringCariesData
            .find((data) => data.type !== null)!
            .type!.toUpperCase() as 'D1' | 'D2' | 'D3' | 'E1' | 'E2')
        : undefined,
      recurrent: false,
      data: recurringCariesData,
    },
    inlay: {
      surfaces: extractSurfaces(inlayData),
      material: inlayData.find((data) => data.material !== null)?.material as
        | 'gold'
        | 'zirconia'
        | 'ceramic'
        | undefined,
      data: inlayData,
    },
    onlay: {
      surfaces: extractSurfaces(onlayData),
      material: onlayData.find((data) => data.material !== null)?.material as
        | 'gold'
        | 'zirconia'
        | 'ceramic'
        | undefined,
      data: onlayData,
    },
    filling: {
      surfaces: extractSurfaces(fillingData),
      material: fillingData.find((data) => data.material !== null)?.material as
        | 'ceramic'
        | 'composite'
        | 'gold'
        | 'amalgam'
        | 'temporary'
        | undefined,
      data: fillingData,
    },
    drifted: {
      surfaces: extractSurfaces(driftedData),
      data: driftedData,
    },
    crown: {
      material: crownData.find((data) => data.material !== null)?.material as
        | 'zirconia'
        | 'pfm'
        | 'pfz'
        | 'metal'
        | 'ceramic'
        | undefined,
      data: crownData,
    },
    erosion: {
      general: erosionData.some((data) => data.surface === null),
      buccal: erosionData.some(
        (data) => data.surface === 'buccal' || data.surface === 'b',
      ),
      palatal: erosionData.some((data) => data.surface === 'palatal'),
      lingual: erosionData.some(
        (data) => data.surface === 'lingual' || data.surface === 'l',
      ),
      occlusal: erosionData.some(
        (data) => data.surface === 'occlusal' || data.surface === 'o',
      ),
      data: erosionData,
    },
    apicoectomy: {
      present: apicoectomyData.length > 0,
      data: apicoectomyData,
    },
    periapical: {
      present: periapicalData.length > 0,
      data: periapicalData,
    },
    periradicular: {
      severity: periradicularData.find((data) => data.type !== null)?.type as
        | 'slight'
        | 'moderate'
        | 'severe'
        | undefined,
      data: periradicularData,
    },
    root_canal: {
      present: rootCanalData.length > 0,
      data: rootCanalData,
    },
    pulpotomy: {
      present: pulpotomyData.length > 0,
      data: pulpotomyData,
    },
    pulpectomy: {
      present: pulpectomyData.length > 0,
      data: pulpectomyData,
    },
    impacted: {
      direction: impactedData.find((data) => data.type !== null)?.type as
        | 'left'
        | 'right'
        | 'down'
        | 'up'
        | undefined,
      data: impactedData,
    },
    supernumerary: {
      present: supernumeraryData.length > 0,
      data: supernumeraryData,
    },
    sinusTract: {
      present: sinusTractData.length > 0,
      data: sinusTractData,
    },
    retainedPrimary: {
      present: retainerData.length > 0,
      data: retainerData,
    },
    recession: {
      type: recessionData.find((data) => data.type !== null)?.type as
        | 'class1'
        | 'class2'
        | 'class3'
        | 'class4'
        | undefined,
      data: recessionData,
    },
    attrition: {
      severity: attritionData.find((data) => data.type !== null)?.type as
        | 'slight'
        | 'moderate'
        | 'severe'
        | undefined,
      data: attritionData,
    },
    abrasion: {
      severity: abrasionData.find((data) => data.type !== null)?.type as
        | 'slight'
        | 'moderate'
        | 'severe'
        | undefined,
      buccal: abrasionData.some(
        (data) => data.surface === 'buccal' || data.surface === 'b',
      ),
      palatal: abrasionData.some(
        (data) => data.surface === 'palatal' || data.surface === 'p',
      ),
      lingual: abrasionData.some(
        (data) => data.surface === 'lingual' || data.surface === 'l',
      ),
      data: abrasionData,
    },
    veneer: {
      present: veneerData.length > 0,
      material: veneerData.find((data) => data.material !== null)?.material as
        | 'ceramic'
        | 'composite'
        | 'zirconia'
        | undefined,
      data: veneerData,
    },
    retainer: {
      present: retainerData.length > 0,
      data: retainerData,
    },
    bridge: (() => {
      const connector = bridgeData.find(
        (data) =>
          (data.condition === 'abutment' || data.condition === 'bridge') &&
          data.type !== null &&
          (data.type === 'left_abutment' ||
            data.type === 'right_abutment' ||
            data.type === 'right_left_abutment' ||
            data.type === 'left_bridge' ||
            data.type === 'right_bridge' ||
            data.type === 'right_left_bridge'),
      )?.type as BridgeConnectorType | undefined
      const materialData = bridgeData.find(
        (data) =>
          data.type === null &&
          data.material !== null &&
          data.material !== undefined,
      )
      const abutmentMaterialData = bridgeData.find(
        (data) =>
          (data.condition === 'abutment' || data.condition === 'bridge') &&
          data.material !== null &&
          data.material !== undefined,
      )
      return {
        connector,
        material: (materialData?.material ?? abutmentMaterialData?.material) as
          | 'zirconia'
          | 'pfm'
          | 'pfz'
          | 'metal'
          | 'ceramic'
          | undefined,
        data: bridgeData,
      }
    })(),
    sealants: {
      present: sealantsData.length > 0,
      data: sealantsData,
    },
    missing: {
      present: missingData.length > 0,
      data: missingData,
    },
    implant: {
      present: implantData.length > 0,
      data: implantData,
    },
    furcation: {
      level: furcationData.find((data) => data.type !== null)?.type as
        | 'C1'
        | 'C2'
        | 'C3'
        | undefined,
      areas:
        furcationData.find((data) => data.type !== null)?.surface ?? undefined,
      data: furcationData,
    },
    crack: {
      types: crackData
        .map((data) => data.type)
        .filter(
          (type): type is NonNullable<typeof type> => type !== null,
        ) as Array<
        | 'vertical_root'
        | 'horizontal_root'
        | 'vertical_crown_buccal'
        | 'vertical_crown_lingual'
        | 'vertical_crown_palatal'
        | 'vertical_crown_occlusal'
        | 'horizontal_crown_buccal'
        | 'horizontal_crown_lingual'
        | 'horizontal_crown_palatal'
        | 'horizontal_crown_occlusal'
      >,
      type: crackData.find((data) => data.type !== null)?.type as
        | 'vertical_root'
        | 'horizontal_root'
        | 'vertical_crown_buccal'
        | 'vertical_crown_lingual'
        | 'vertical_crown_palatal'
        | 'vertical_crown_occlusal'
        | 'horizontal_crown_buccal'
        | 'horizontal_crown_lingual'
        | 'horizontal_crown_palatal'
        | 'horizontal_crown_occlusal'
        | undefined,
      recurrent: crackData.some((data) => data.recurrent === true),
      data: crackData,
    },
    fracture: {
      types: fractureData
        .map((data) => data.type)
        .filter(
          (type): type is NonNullable<typeof type> => type !== null,
        ) as Array<
        | 'mesiobuccal'
        | 'distobuccal'
        | 'mesiolingual'
        | 'distolingual'
        | 'mesiopalatal'
        | 'distopalatal'
        | 'incisal'
        | 'mifp'
        | 'difp'
        | 'mifl'
        | 'difl'
      >,
      type: fractureData.find((data) => data.type !== null)?.type as
        | 'mesiobuccal'
        | 'distobuccal'
        | 'mesiolingual'
        | 'distolingual'
        | 'mesiopalatal'
        | 'distopalatal'
        | 'incisal'
        | 'mifp'
        | 'difp'
        | 'mifl'
        | 'difl'
        | undefined,
      recurrent: fractureData.some((data) => data.recurrent === true),
      data: fractureData,
    },
  }
}

/**
 * Remove all conditions for a specific tooth
 */
export const removeAllConditionsForTooth = (
  dental_data: IDentalData[],
  toothNumber: number,
): IDentalData[] => {
  return dental_data.filter((data) => {
    // Handle both string and number types for tooth_number
    const dataToothNumber =
      typeof data.tooth_number === 'string'
        ? data.tooth_number
        : String(data.tooth_number)
    return dataToothNumber !== toothNumber.toString()
  })
}

/**
 * Replace all conditions for a tooth with a single condition
 */
export const replaceToothConditions = (
  dental_data: IDentalData[],
  toothNumber: number,
  condition: string,
  type: string | null = null,
  material: string | null = null,
  surface: string | null = null,
  recurrent: boolean = false,
): IDentalData[] => {
  const filtered = removeAllConditionsForTooth(dental_data, toothNumber)
  return addConditionToDentalData(
    filtered,
    toothNumber,
    condition,
    type,
    material,
    surface,
    recurrent,
  )
}

/**
 * Update the recurrent flag for all conditions of a specific type on a tooth
 */
export const updateRecurrentFlag = (
  dental_data: IDentalData[],
  toothNumber: number,
  condition: string,
  recurrent: boolean,
): IDentalData[] => {
  const toothNumberStr = toothNumber.toString()
  return dental_data.map((data) => {
    const dataToothNumber =
      typeof data.tooth_number === 'string'
        ? data.tooth_number
        : String(data.tooth_number)
    if (dataToothNumber === toothNumberStr && data.condition === condition) {
      return { ...data, recurrent }
    }
    return data
  })
}
